import KeepLink from './KeepLink';
import type { Lang } from '../hooks/useLang';
import './HomeDivergencePanel.css';

interface Props {
  lang: Lang;
}

// Numbers reflect the current tier distribution.  When re-scoring shifts
// the tiers, update the STATS array below.
const STATS: {
  key: string;
  count: string;
  labelEn: string;
  labelTa: string;
  tone: 't4' | 't3' | 't2' | 't1' | 't0';
}[] = [
    { key: 't4', count: '4',     labelEn: 'read as entirely different subjects', labelTa: 'முற்றிலும் வேறு பொருள்', tone: 't4' },
    { key: 't3', count: '14',    labelEn: 'with imported frameworks',             labelTa: 'புற வடிவங்கள் கூட்டப்பட்டவை', tone: 't3' },
    { key: 't2', count: '198',   labelEn: 'with clashing glosses',                labelTa: 'பொருள் மோதுபவை', tone: 't2' },
    { key: 't1', count: '18',    labelEn: 'with opposed emphasis',                labelTa: 'எதிர் நோக்கமுள்ளவை', tone: 't1' },
    { key: 't0', count: '1,096', labelEn: 'in quiet agreement',                    labelTa: 'அமைதியான உடன்பாடு', tone: 't0' },
  ];

export default function HomeDivergencePanel({ lang }: Props) {
  return (
    <section
      className="home__dv"
      aria-labelledby="home-dv-heading"
      {...(lang === 'ta' ? { lang: 'ta' } : {})}
    >
      <p className="home__dv-eyebrow">
        <span className="home__dv-badge">New</span>
        <span>{lang === 'ta' ? 'மணக்குடவர் × பரிமேலழகர்' : 'Manakkudavar × Parimelazhagar'}</span>
      </p>

      <h2 id="home-dv-heading" className="home__dv-title">
        {lang === 'ta' ? (
          <>
            <span lang="ta" className="home__dv-title-lead">
              உரையாசிரியர்கள் எங்கே பிரிகிறார்கள்
            </span>
            <span className="home__dv-title-sub">Where the commentators part ways</span>
          </>
        ) : (
          <>
            <span className="home__dv-title-lead">Where the commentators part ways</span>
            <span lang="ta" className="home__dv-title-sub">
              உரையாசிரியர்கள் எங்கே பிரிகிறார்கள்
            </span>
          </>
        )}
      </h2>

      <p className="home__dv-lede" {...(lang === 'ta' ? { lang: 'ta' } : {})}>
        {lang === 'ta' ? (
          <>
            முதன்முறையாக, ஒவ்வொரு குறளின் மீதும் மணக்குடவரும் (~10ஆம்
            நூற்றாண்டு) பரிமேலழகரும் (~13ஆம் நூற்றாண்டு) எத்தனை ஆழமாக
            வேறுபடுகிறார்கள் என்பதன் வரைபடம். இரு பழம் குரல்கள் —
            சிலநேரம் ஒன்றே சொல்கிறார்கள், சிலநேரம் ஒரு வரியை
            முற்றிலும் இரண்டு குறள்களாகப் படிக்கிறார்கள்.
          </>
        ) : (
          <>
            For the first time, every kural mapped by how deeply Manakkudavar
            (<span lang="ta">மணக்குடவர்</span>, ~10th c.) and Parimelazhagar
            (<span lang="ta">பரிமேலழகர்</span>, ~13th c.) disagree. Two
            ancient voices — sometimes saying the same thing, sometimes
            reading a single line as two different verses.
          </>
        )}
      </p>

      <MiniBraid />

      <ul className="home__dv-stats" role="list">
        {STATS.map((s) => (
          <li key={s.key} className={`home__dv-stat home__dv-stat--${s.tone}`}>
            <span className="home__dv-stat-num">{s.count}</span>
            <span className="home__dv-stat-label">
              {lang === 'ta' ? s.labelTa : s.labelEn}
            </span>
          </li>
        ))}
      </ul>

      <div className="home__dv-cta-row">
        <KeepLink to="/divergence" className="home__dv-cta">
          <span>{lang === 'ta' ? 'இரு இழைகளைப் பின்தொடர்க' : 'Follow the two threads'}</span>
          <span aria-hidden="true">→</span>
        </KeepLink>
      </div>
    </section>
  );
}

/**
 * Two threads (palm-green M, saffron P) drawn as smooth curves that
 * separate at four "hotspots" and rejoin. Draw-in animation on mount;
 * respects prefers-reduced-motion via CSS.
 */
function MiniBraid() {
  return (
    <svg
      className="home__dv-braid"
      viewBox="0 0 900 110"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <filter id="home-dv-soft" x="-10%" y="-30%" width="120%" height="160%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
      </defs>

      {/* Glow underlays */}
      <path
        className="home__dv-braid-glow home__dv-braid-glow--m"
        d="M0,55 C90,55 130,10 200,10 C270,10 300,55 380,55 C460,55 490,20 560,20 C630,20 660,55 730,55 C800,55 830,30 900,30"
        filter="url(#home-dv-soft)"
      />
      <path
        className="home__dv-braid-glow home__dv-braid-glow--p"
        d="M0,55 C90,55 130,100 200,100 C270,100 300,55 380,55 C460,55 490,90 560,90 C630,90 660,55 730,55 C800,55 830,80 900,80"
        filter="url(#home-dv-soft)"
      />

      {/* Foreground threads */}
      <path
        className="home__dv-braid-thread home__dv-braid-thread--m"
        d="M0,55 C90,55 130,10 200,10 C270,10 300,55 380,55 C460,55 490,20 560,20 C630,20 660,55 730,55 C800,55 830,30 900,30"
      />
      <path
        className="home__dv-braid-thread home__dv-braid-thread--p"
        d="M0,55 C90,55 130,100 200,100 C270,100 300,55 380,55 C460,55 490,90 560,90 C630,90 660,55 730,55 C800,55 830,80 900,80"
      />

      {/* Meeting-point dots where the threads cross the midline */}
      <circle className="home__dv-braid-node" cx="0"   cy="55" r="2.5" />
      <circle className="home__dv-braid-node" cx="380" cy="55" r="2.5" />
      <circle className="home__dv-braid-node" cx="730" cy="55" r="2.5" />
    </svg>
  );
}
